import { useRef } from "react";

const API_BASE = `${process.env.REACT_APP_VIDEOSDK_URL}/ai/v1/face-verification`;

const FAILURE_THRESHOLD = 3;

/**
 * Shared face-verification engine used by both the recorder (owner mode) and the
 * client (delegate mode).
 *
 * - Owner mode (recorder): runs the full sequential pipeline
 *   detect-faces -> detect-spoof -> facematch off the customer's streamed frames,
 *   publishes the verdict channels, and tracks a per-API consecutive-failure
 *   counter. After FAILURE_THRESHOLD failures on a single API it delegates ONLY
 *   that API to the client via the DELEGATE_API channel and pauses it locally
 *   until the client reports success (handback).
 *
 * - Delegate mode (client): runs exactly one API on its own local frames until it
 *   succeeds (used only as a fallback when the recorder asks).
 *
 * A "failure" is ANY non-success result: an API error (throw / non-2xx / 400 /
 * null) OR a negative verdict (faces !== 1, spoof_detected === true,
 * verified === false). A success resets that API's counter.
 */
export default function useFaceVerification({
  mode = "owner",
  captureImage,
  webcamOn,
  token,
  roomId,
  abortSignalRef,
  currentPage,
  referenceImageRef,
  publishers,
  callbacks,
}) {
  // pipeline progress flags
  const spoofCheckDoneRef = useRef(false);
  const faceDetectionDoneRef = useRef(false);
  const faceMatchDoneRef = useRef(false);

  // per-API consecutive-failure counters + active delegation
  const faceFailRef = useRef(0);
  const spoofFailRef = useRef(0);
  const matchFailRef = useRef(0);
  const delegatedApiRef = useRef(null);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // ---- verdict publishing (owner only) ----------------------------------
  // We also notify the recorder's own context directly via callbacks so its UI
  // updates without depending on pubsub echoing self-published messages back.
  const publishAgreed = (isAgreed) => {
    callbacks?.onAgreed?.(isAgreed);
    return (
      publishers?.agreedPublish &&
      publishers.pubsubPublishMethod(
        publishers.agreedPublish,
        "Agreed",
        { persist: true },
        { isAgreed },
      )
    );
  };

  const publishVerified = (isVerified) => {
    callbacks?.onVerified?.(isVerified);
    return (
      publishers?.faceVerifiedPublish &&
      publishers.pubsubPublishMethod(
        publishers.faceVerifiedPublish,
        "Verified",
        { persist: true },
        { isVerified },
      )
    );
  };

  const publishError = (err) => {
    callbacks?.onError?.(err);
    return (
      publishers?.faceDetectErrorPublish &&
      publishers.pubsubPublishMethod(
        publishers.faceDetectErrorPublish,
        "Error",
        { persist: true },
        { err },
      )
    );
  };

  const publishAbort = (isAbort) =>
    publishers?.abortSignalPublish &&
    publishers.pubsubPublishMethod(
      publishers.abortSignalPublish,
      "Abort",
      { persist: true },
      { isAbort },
    );

  // ---- raw API calls -----------------------------------------------------
  const verifyNumberOfFaces = async (sendImage) => {
    const signal = abortSignalRef?.current?.signal;

    if (
      (currentPage === "PersonalDetails" || currentPage === "PolicyDetails") &&
      faceDetectionDoneRef.current
    ) {
      return 1;
    }

    try {
      const res = await fetch(`${API_BASE}/detect-faces`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img: sendImage, metadata: { roomId } }),
        signal,
      });

      if (res.status === 400) {
        console.error("No faces detected or bad request.");
        return null;
      }

      const { number_of_faces } = await res.json();
      if (
        (currentPage === "PersonalDetails" ||
          currentPage === "PolicyDetails") &&
        number_of_faces === 1
      ) {
        faceDetectionDoneRef.current = true;
      }
      return number_of_faces;
    } catch (error) {
      if (error.name === "AbortError") return null;
      console.error("Error during face detection:", error);
      return null;
    }
  };

  const detectSpoof = async (sendImage) => {
    const signal = abortSignalRef?.current?.signal;
    try {
      const res = await fetch(`${API_BASE}/detect-spoof`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img: sendImage, metadata: { roomId } }),
        signal,
      });

      if (res.status === 400) {
        console.error("No faces detected or bad request.");
        return true;
      }

      const { spoof_detected } = await res.json();
      return spoof_detected;
    } catch (error) {
      if (error.name === "AbortError") return true;
      console.error("Error during spoof detection:", error);
      return true;
    }
  };

  const faceMatch = async (img1, img2) => {
    const signal = abortSignalRef?.current?.signal;
    try {
      const res = await fetch(`${API_BASE}/verify`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img1, img2, metadata: { roomId } }),
        signal,
      });

      if (res.status === 400) {
        console.error("Error in face verification: Bad request.");
        return false;
      }

      const { verified } = await res.json();
      return verified;
    } catch (error) {
      if (error.name === "AbortError") return false;
      console.error("Error during face match verification:", error);
      return false;
    }
  };

  // ---- delegation bookkeeping (owner) ------------------------------------
  const noteFailure = (ref, apiName) => {
    ref.current += 1;
    callbacks?.onFailure?.(apiName, ref.current);
    if (ref.current >= FAILURE_THRESHOLD && delegatedApiRef.current !== apiName) {
      delegatedApiRef.current = apiName;
      callbacks?.onDelegate?.(apiName, ref.current);
      publishers?.delegateApiPublish &&
        publishers.pubsubPublishMethod(
          publishers.delegateApiPublish,
          "DelegateApi",
          { persist: true },
          { apiName, delegate: true, currentPage },
        );
    }
  };

  // Called on the recorder when the client reports a successful fallback run.
  const handback = (apiName) => {
    if (apiName === "faces") faceFailRef.current = 0;
    else if (apiName === "spoof") spoofFailRef.current = 0;
    else if (apiName === "match") matchFailRef.current = 0;

    if (delegatedApiRef.current === apiName) {
      delegatedApiRef.current = null;
      publishers?.delegateApiPublish &&
        publishers.pubsubPublishMethod(
          publishers.delegateApiPublish,
          "DelegateApi",
          { persist: true },
          { apiName, delegate: false, currentPage },
        );
    }
  };

  // Clear all delegation/counters (call on abort or page reset).
  const resetDelegation = () => {
    faceFailRef.current = 0;
    spoofFailRef.current = 0;
    matchFailRef.current = 0;
    delegatedApiRef.current = null;
  };

  // ---- owner pipeline (recorder) -----------------------------------------
  async function runOwnerPipeline() {
    if (!webcamOn) {
      publishAgreed(false);
      publishAbort(false);
      publishError("NO_CAMERA_PERMISSIONS");
      return;
    }

    // 1) FACE DETECTION
    if (delegatedApiRef.current === "faces") return; // waiting for client

    let base64Data = await captureImage(500, 400);
    let sendImage = "data:image/jpeg;base64," + base64Data;
    const faces = await verifyNumberOfFaces(sendImage);

    if (faces === 1) {
      faceFailRef.current = 0;
    } else {
      noteFailure(faceFailRef, "faces");
      publishAgreed(false);
      publishAbort(false);
      publishError(faces > 1 ? "MULTIPLE_FACES_DETECTED" : "NO_FACE_DETECTED");
      return;
    }

    // 2) SPOOF / LIVENESS
    if (!spoofCheckDoneRef.current) {
      let spoofResponse = true;
      while (spoofResponse) {
        if (delegatedApiRef.current === "spoof") return; // waiting for client

        base64Data = await captureImage(500, 400);
        sendImage = "data:image/jpeg;base64," + base64Data;
        spoofResponse = await detectSpoof(sendImage);

        if (spoofResponse) {
          noteFailure(spoofFailRef, "spoof");
          publishAgreed(false);
          publishError("SPOOF_DETECTED");
          publishAbort(false);
          if (delegatedApiRef.current === "spoof") return; // just delegated
          await delay(3000);
        } else {
          spoofFailRef.current = 0;
        }
      }
      spoofCheckDoneRef.current = true;
    }

    // 3) REFERENCE CAPTURE (PersonalDetails) or FACE MATCH (later pages)
    if (currentPage === "PersonalDetails") {
      if (referenceImageRef && referenceImageRef.current == null) {
        referenceImageRef.current = sendImage;
      }
      publishAgreed(true);
      publishVerified(true);
    } else {
      if (currentPage === "PolicyDetails" && faceMatchDoneRef.current) return;
      if (delegatedApiRef.current === "match") return; // waiting for client

      const matchResponse = await faceMatch(
        sendImage,
        referenceImageRef?.current,
      );
      if (matchResponse) {
        matchFailRef.current = 0;
        publishAgreed(true);
        publishVerified(true);
        if (currentPage === "PolicyDetails") faceMatchDoneRef.current = true;
      } else {
        noteFailure(matchFailRef, "match");
        publishAgreed(false);
        publishAbort(false);
        publishError("FACE_MATCH_UNSUCESSFUL");
      }
    }
  }

  // ---- single-API delegated runner (client fallback) ---------------------
  // Returns true on success for the requested API, false otherwise.
  async function runDelegatedApi(apiName) {
    if (!webcamOn) return false;

    const base64Data = await captureImage(500, 400);
    const sendImage = "data:image/jpeg;base64," + base64Data;

    if (apiName === "faces") {
      const faces = await verifyNumberOfFaces(sendImage);
      return faces === 1;
    }
    if (apiName === "spoof") {
      const spoof = await detectSpoof(sendImage);
      return spoof === false;
    }
    if (apiName === "match") {
      const verified = await faceMatch(sendImage, referenceImageRef?.current);
      return verified === true;
    }
    return false;
  }

  return {
    runOwnerPipeline,
    runDelegatedApi,
    handback,
    resetDelegation,
    delegatedApiRef,
    faceFailRef,
    spoofFailRef,
    matchFailRef,
    spoofCheckDoneRef,
    faceDetectionDoneRef,
    faceMatchDoneRef,
  };
}
